/**
 * VisibilityPanel — J7-W2 task 4 (PRD §1.3, §12.3.10).
 *
 * Reusable panel that renders the visibility-scope controls for any
 * ROROS entity. Used by:
 *   - ActorDetailPage (Visibility tab)
 *   - RoleContractEditor (Tool Access Policy section)
 *   - any future surface that needs the same scope picker
 *
 * The component is a pure presentational + react-query wrapper — it does
 * NOT duplicate the control logic between consumers. The brief calls this
 * out explicitly: "VisibilityPanel is a SHARED component — do NOT
 * duplicate it between ActorDetailPage and RoleContractEditor."
 *
 * Backend wiring: GET/PUT/DELETE /api/v1/visibility/:entityType/:entityId
 * via the existing RorosVisibilityService. No new endpoints introduced.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { RadioGroup } from '@/components/ui/Radio';
import { MultiSelect, type MultiSelectOption } from '@/components/ui/MultiSelect';
import { Badge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { Eye, Lock, Users, Trash2 } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import {
  useRorosVisibility,
  useRorosVisibilitySet,
  useRorosVisibilityRemove,
} from '@/hooks/roros/visibility';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosDomains } from '@/hooks/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';

export type VisibilityScope = 'org' | 'restricted' | 'custom';

export interface VisibilityPanelProps {
  /** Entity type the visibility scope is anchored to (e.g. 'actor', 'role_contract'). */
  entityType: string;
  /** Entity id (UUID). */
  entityId: string;
  /**
   * Optional list of actor ids to exclude from the picker (typically the
   * subject actor itself when scoping its own visibility — would be
   * meaningless to share-with-self).
   */
  excludeActorIds?: string[];
  /**
   * Optional override for the panel title. Defaults to "Visibility".
   */
  title?: string;
  /** Optional helper / description shown under the title. */
  description?: string;
  /** Optional className applied to the wrapper Card. */
  className?: string;
}

export function VisibilityPanel({
  entityType,
  entityId,
  excludeActorIds = [],
  title,
  description,
  className,
}: VisibilityPanelProps) {
  const visibilityQuery = useRorosVisibility(entityType, entityId);
  const setMutation = useRorosVisibilitySet();
  const removeMutation = useRorosVisibilityRemove();
  const actorsQuery = useRorosActors();
  const domainsQuery = useRorosDomains();

  const [scope, setScope] = useState<VisibilityScope>('org');
  const [allowedActorIds, setAllowedActorIds] = useState<string[]>([]);
  const [allowedDomainIds, setAllowedDomainIds] = useState<string[]>([]);
  const [deniedActorIds, setDeniedActorIds] = useState<string[]>([]);

  // Hydrate local state from server payload.
  // Backend returns `null` when no override exists → org scope (default).
  useEffect(() => {
    const data = visibilityQuery.data;
    if (!data) {
      setScope('org');
      setAllowedActorIds([]);
      setAllowedDomainIds([]);
      setDeniedActorIds([]);
      return;
    }
    setAllowedActorIds(data.allowedActorIds ?? []);
    setAllowedDomainIds(data.allowedDomainIds ?? []);
    setDeniedActorIds(data.deniedActorIds ?? []);
    if (data.visibilityMode === 'restricted') setScope('restricted');
    else if (data.visibilityMode === 'custom') setScope('custom');
    else setScope('org');
  }, [visibilityQuery.data]);

  const actorOptions: MultiSelectOption[] = useMemo(() => {
    const list: RorosActorDTO[] = (actorsQuery.data as RorosActorDTO[] | undefined) ?? [];
    return list
      .filter((a) => !excludeActorIds.includes(a.id))
      .map((a) => ({
        value: a.id,
        label: a.name,
        description: a.actorType,
      }));
  }, [actorsQuery.data, excludeActorIds]);

  const domainOptions: MultiSelectOption[] = useMemo(() => {
    return (domainsQuery.data ?? []).map((d) => ({ value: d.id, label: d.name }));
  }, [domainsQuery.data]);

  const handleSave = () => {
    if (scope === 'org') {
      // Org scope = no override row. Remove if one exists.
      removeMutation.mutate(
        { entityType, entityId },
        {
          onSuccess: () => showToast.success(t('Visibility set to organization scope')),
          onError: (err) =>
            showToast.error(err instanceof Error ? err.message : t('Failed to update visibility')),
        },
      );
      return;
    }
    setMutation.mutate(
      {
        entityType,
        entityId,
        payload: {
          visibilityMode: scope,
          allowedActorIds,
          allowedDomainIds: scope === 'custom' ? allowedDomainIds : [],
          deniedActorIds: scope === 'custom' ? deniedActorIds : [],
        },
      },
      {
        onSuccess: () => showToast.success(t('Visibility scope saved')),
        onError: (err) =>
          showToast.error(err instanceof Error ? err.message : t('Failed to update visibility')),
      },
    );
  };

  const isSaving = setMutation.isPending || removeMutation.isPending;
  const isLoading = visibilityQuery.isLoading || actorsQuery.isLoading || domainsQuery.isLoading;

  return (
    <Card className={className} data-testid="visibility-panel">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>{title ?? t('Visibility')}</CardTitle>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
          <Badge variant="outline" className="text-xs">
            {scope === 'org' && (
              <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{t('Org-wide')}</span>
            )}
            {scope === 'restricted' && (
              <span className="flex items-center gap-1"><Lock className="h-3 w-3" />{t('Restricted')}</span>
            )}
            {scope === 'custom' && (
              <span className="flex items-center gap-1"><Users className="h-3 w-3" />{t('Custom share')}</span>
            )}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{t('Loading...')}</p>
        ) : (
          <div className="space-y-4">
            <div data-testid="visibility-scope-group">
              <RadioGroup
                name="visibility-scope"
                value={scope}
                onChange={(v) => setScope(v as VisibilityScope)}
                label={t('Visibility scope')}
                options={[
                  {
                    value: 'org',
                    label: t('Organization scope (default)'),
                    hint: t('Visible to all org members per their role permissions.'),
                  },
                  {
                    value: 'restricted',
                    label: t('Per-item restricted'),
                    hint: t('Visible only to the actors you list below.'),
                  },
                  {
                    value: 'custom',
                    label: t('Share with specific actors and domains'),
                    hint: t('Granular allow + deny lists, scoped to the selected domains.'),
                  },
                ]}
              />
            </div>

            {scope === 'restricted' && (
              <div className="pt-2">
                <MultiSelect
                  label={t('Visible to actors')}
                  options={actorOptions}
                  value={allowedActorIds}
                  onChange={setAllowedActorIds}
                  placeholder={t('Pick at least one actor')}
                />
                {allowedActorIds.length === 0 && (
                  <Alert variant="warning" className="mt-2">
                    {t('Restricted mode without listed actors hides this entity from everyone except admins.')}
                  </Alert>
                )}
              </div>
            )}

            {scope === 'custom' && (
              <div className="pt-2 space-y-3">
                <MultiSelect
                  label={t('Allowed actors')}
                  options={actorOptions}
                  value={allowedActorIds}
                  onChange={setAllowedActorIds}
                />
                <MultiSelect
                  label={t('Allowed domains')}
                  options={domainOptions}
                  value={allowedDomainIds}
                  onChange={setAllowedDomainIds}
                />
                <MultiSelect
                  label={t('Denied actors (overrides allowed lists)')}
                  options={actorOptions}
                  value={deniedActorIds}
                  onChange={setDeniedActorIds}
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              {visibilityQuery.data && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScope('org')}
                  disabled={isSaving}
                  data-testid="visibility-reset-button"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  {t('Reset to org-wide')}
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={isSaving}
                data-testid="visibility-save-button"
              >
                {isSaving ? t('Saving...') : t('Save visibility')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
